/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  forwardRef,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Platform } from '@angular/cdk/platform';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { UmssIconComponent } from '../icon/icon.component';
import { UmssTimePickerClockComponent } from './time-picker-clock/time-picker-clock.component';
import {
  OverlayScrollBehavior,
  OverlayScrollService
} from '../../services/overlay-scroll.service';
import {
  ShortTimeString,
  isValidShortTimeString,
  parseShortTimeString,
  buildShortTimeString
} from '@umss/shared/utils';

@Component({
  selector: 'umss-time-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    UmssIconComponent,
    UmssTimePickerClockComponent
  ],
  templateUrl: './time-picker.component.html',
  styleUrl: './time-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssTimePickerComponent),
      multi: true
    },
    provideNgxMask()
  ]
})
export class UmssTimePickerComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @HostBinding('style.display') display = 'contents';
  @ViewChild('nativeTimeInput')
  nativeTimeInputRef?: ElementRef<HTMLInputElement>;
  private elementRef = inject(ElementRef);
  private platform = inject(Platform);
  private overlayScrollService = inject(OverlayScrollService);

  protected readonly isMobile = this.platform.ANDROID || this.platform.IOS;

  private readonly _fallbackId = `umss-time-picker-${Math.random().toString(36).slice(2)}`;
  get inputId(): string {
    return this.id ?? this._fallbackId;
  }

  // Close when clicking outside the entire time-picker component
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isPopoverOpen) return;
    const clickedInside = this.elementRef.nativeElement.contains(
      event.target as Node
    );
    if (!clickedInside) {
      this.isPopoverOpen = false;
      this._onTouched();
      this.onBlur.emit();
      this._unregisterScrollBehavior();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isPopoverOpen) {
      this.calculatePosition();
    }
  }

  /** Internal FormControl drives the masked text input */
  inputControl = new FormControl<string>('', { nonNullable: true });
  private inputSubscription?: { unsubscribe: () => void };

  ngOnInit(): void {
    this.inputSubscription = this.inputControl.valueChanges.subscribe(
      (value) => {
        this.syncClockFromInput(value);
        const parsed = this.parseToShortTimeString(value);
        this._onChange(parsed);
        this.timeChange.emit(parsed);
      }
    );
  }

  ngOnDestroy(): void {
    this.inputSubscription?.unsubscribe();
    this._unregisterScrollBehavior();
  }

  /** Content */
  @Input() label?: string;
  @Input() placeholder = 'HH:mm';
  @Input() helperText?: string;
  @Input() errorMessage?: string;

  /** Variant */
  @Input() size: 'Mini' | 'Small' | 'Regular' | 'Large' = 'Regular';

  /** State */
  @Input() disabled = false;
  @Input() required = false;
  @Input() error = false;

  /** HTML */
  @Input() id?: string;
  @Input() name?: string;
  @Input() scrollBehavior: OverlayScrollBehavior = 'close';

  /** Programmatic value binding (alternative to using formControl / ngModel) */
  @Input() set value(v: string | null | undefined) {
    if (v && isValidShortTimeString(v)) {
      this.writeValue(v);
    } else {
      this.writeValue(null);
    }
  }

  /** Outputs */
  @Output() timeChange = new EventEmitter<ShortTimeString | null>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onFocus = new EventEmitter<void>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onBlur = new EventEmitter<void>();

  /** Popover + clock state */
  protected isPopoverOpen = false;
  protected dropdownStyle: Record<string, string> = {};
  protected clockHours = signal(0);
  protected clockMinutes = signal(0);
  protected clockView = signal<'hours' | 'minutes'>('hours');
  private _overlayScrollId?: symbol;

  /** CVA implementation */
  private _onChange: (value: ShortTimeString | null) => void = () => {};
  private _onTouched: () => void = () => {};

  writeValue(value: ShortTimeString | null): void {
    const strValue = value ?? '';
    this.inputControl.setValue(strValue, { emitEvent: false });
    if (value && isValidShortTimeString(value)) {
      const { hours, minutes } = parseShortTimeString(value);
      this.clockHours.set(hours);
      this.clockMinutes.set(minutes);
    } else {
      this.clockHours.set(0);
      this.clockMinutes.set(0);
    }
  }

  registerOnChange(fn: (value: ShortTimeString | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.inputControl.disable({ emitEvent: false });
    } else {
      this.inputControl.enable({ emitEvent: false });
    }
  }

  /** Template event handlers */
  onInputFocus(): void {
    if (this.disabled) return;
    if (!this.isMobile) {
      this.clockView.set('hours');
      this.openDropdown();
    }
    this.onFocus.emit();
  }

  onInputBlur(): void {
    if (this.disabled) return;
    // Don't close popover on blur — closing is handled by document:click
    this._onTouched();
    this.onBlur.emit();
  }

  onNativeTimeChange(event: Event): void {
    if (this.disabled) return;
    const input = event.target as HTMLInputElement;
    const value = input.value; // Format: HH:mm

    if (value && value.length === 5) {
      // value is in HH:mm format, which is a valid ShortTimeString
      this.inputControl.setValue(value, { emitEvent: false });
      this.syncClockFromInput(value);
      const parsed = this.parseToShortTimeString(value);
      this._onChange(parsed);
      this.timeChange.emit(parsed);
    } else if (!value) {
      // Clear the value
      this.inputControl.setValue('', { emitEvent: false });
      this.clockHours.set(0);
      this.clockMinutes.set(0);
      this._onChange(null);
      this.timeChange.emit(null);
    }
  }

  onClockIconClick(): void {
    if (this.disabled) return;
    if (this.isMobile) {
      // On mobile, the icon opens the native picker
      this.nativeTimeInputRef?.nativeElement.showPicker?.();
      this.nativeTimeInputRef?.nativeElement.focus();
      return;
    }
    if (this.isPopoverOpen) {
      this.isPopoverOpen = false;
      this._unregisterScrollBehavior();
    } else {
      this.clockView.set('hours');
      this.openDropdown();
    }
  }

  onPopoverClose(): void {
    this._onTouched();
  }

  onHourChange(h: number): void {
    this.clockHours.set(h);
    const formatted = buildShortTimeString(h, this.clockMinutes());
    if (formatted) {
      this.inputControl.setValue(formatted, { emitEvent: true });
    }
  }

  onMinuteChange(m: number): void {
    this.clockMinutes.set(m);
    const formatted = buildShortTimeString(this.clockHours(), m);
    if (formatted) {
      this.inputControl.setValue(formatted, { emitEvent: true });
    }
    this.isPopoverOpen = false;
    this._unregisterScrollBehavior();
  }

  onClockViewChange(v: 'hours' | 'minutes'): void {
    this.clockView.set(v);
  }

  private openDropdown(): void {
    this.calculatePosition();
    this.isPopoverOpen = true;
    this._registerScrollBehavior();
  }

  private _registerScrollBehavior(): void {
    this._unregisterScrollBehavior();
    this._overlayScrollId = this.overlayScrollService.register({
      behavior: this.scrollBehavior,
      onFollow: () => this.calculatePosition(),
      onClose: () => {
        this.isPopoverOpen = false;
        this._onTouched();
        this.onBlur.emit();
        this._unregisterScrollBehavior();
      }
    });
  }

  private _unregisterScrollBehavior(): void {
    if (this._overlayScrollId !== undefined) {
      this.overlayScrollService.unregister(this._overlayScrollId);
      this._overlayScrollId = undefined;
    }
  }

  private calculatePosition(): void {
    const host = this.elementRef.nativeElement;
    const trigger = host.querySelector('.input-wrapper');
    if (!trigger) return;

    const rect = (trigger as HTMLElement).getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const popoverHeight = 360;
    const popoverWidth = 288;

    const style: Record<string, string> = {
      'min-width': `${rect.width}px`
    };

    // Vertical: prefer below, flip above if not enough space
    if (
      viewportHeight - rect.bottom < popoverHeight &&
      rect.top > viewportHeight - rect.bottom
    ) {
      style['bottom'] = `${viewportHeight - rect.top + 4}px`;
    } else {
      style['top'] = `${rect.bottom + 4}px`;
    }

    // Horizontal: prefer left-aligned, flip if not enough space
    if (viewportWidth - rect.left < popoverWidth && rect.right > popoverWidth) {
      style['right'] = `${viewportWidth - rect.right}px`;
    } else {
      style['left'] = `${rect.left}px`;
    }

    this.dropdownStyle = style;
  }

  /** True when inputControl has a valid time value.
   *  Used to control the display of the hand in the clock. */
  protected get hasClockValue(): boolean {
    return isValidShortTimeString(this.inputControl.value);
  }

  get iconSize(): string {
    switch (this.size) {
      case 'Mini':
        return '14px';
      case 'Small':
        return '16px';
      case 'Large':
        return '20px';
      default:
        return '18px';
    }
  }

  /** Parses the masked text input to a valid ShortTimeString or null */
  private parseToShortTimeString(value: string): ShortTimeString | null {
    if (!value || value.length < 5) return null;
    return isValidShortTimeString(value) ? (value as ShortTimeString) : null;
  }

  /** Syncs clock state from text input (only when fully valid) */
  private syncClockFromInput(value: string): void {
    if (!isValidShortTimeString(value)) return;
    const { hours, minutes } = parseShortTimeString(value as ShortTimeString);
    this.clockHours.set(hours);
    this.clockMinutes.set(minutes);
  }
}
